import { Textarea } from "@/components/ui/textarea";
import { useState, type KeyboardEvent } from "react";

import { MarkdownView } from "@/lib/markdown-view";
import { WikiLabels } from "@/lib/labels/wiki";
import { cn } from "@/lib/utils";

type Tab = "write" | "preview";

export function WikiEditor({
	value,
	onChange,
	disabled,
	autoFocus,
	onKeyDown,
}: {
	value: string;
	onChange: (value: string) => void;
	disabled: boolean;
	autoFocus?: boolean;
	onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
}) {
	const [tab, setTab] = useState<Tab>("write");

	return (
		<div className="flex flex-col gap-2">
			<div role="tablist" className="inline-flex w-fit rounded-lg border bg-background p-0.5">
				{WikiLabels.tabs.map((option) => (
					<button
						key={option.value}
						type="button"
						role="tab"
						aria-selected={tab === option.value}
						onClick={() => setTab(option.value)}
						className={cn(
							"inline-flex h-11 items-center rounded-md px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring md:h-auto md:px-2.5 md:py-1",
							tab === option.value
								? "bg-primary text-primary-foreground"
								: "text-muted-foreground hover:text-foreground",
						)}
					>
						{option.label}
					</button>
				))}
			</div>

			{tab === "write" && (
				<Textarea
					value={value}
					onChange={(event) => onChange(event.target.value)}
					onKeyDown={onKeyDown}
					rows={14}
					disabled={disabled}
					autoFocus={autoFocus}
					placeholder={WikiLabels.contentPlaceholder}
					className="font-mono leading-relaxed"
				/>
			)}

			{tab === "preview" && (
				<div className="min-h-44 rounded-md border border-dashed bg-background/60 px-4 py-3">
					{!!value.trim() && <MarkdownView md={value} />}
					{!value.trim() && (
						<p className="text-xs italic text-muted-foreground">{WikiLabels.previewEmpty}</p>
					)}
				</div>
			)}
		</div>
	);
}
